import { useEffect, useRef } from 'react'
import mapboxgl from 'mapbox-gl'
import { useMapboxMapContext } from '../contexts/MapboxMapContext'
import { deepEqual } from '@/shared/utils/deepEqual'

export interface UseRouteLayerProps {
  routes: any[]
  selectedRoute?: any
}

/**
 * Hook for managing route layer rendering
 * Handles route GeoJSON source and layer creation/updates
 */
export function useRouteLayer({
  routes,
  selectedRoute
}: UseRouteLayerProps) {
  const { map, isLoaded, styleLoaded } = useMapboxMapContext()
  const previousStyleKeyRef = useRef<string>('')
  
  // Stable references to prevent unnecessary layer updates
  const stableRoutesRef = useRef<any[]>([])
  const previousRoutesRef = useRef<any[]>([])
  const previousSelectedRouteIdRef = useRef<string | undefined>(undefined)
  const previousFeatureCountRef = useRef<number>(0)

  // Helper to compare route geometry for changes (even when route IDs are same)
  const routesGeometryChanged = (prev: any[], curr: any[]): boolean => {
    if (prev.length !== curr.length) return true
    
    const prevMap = new Map(prev.map(r => [r.id, r]))
    
    for (const currRoute of curr) {
      const prevRoute = prevMap.get(currRoute.id)
      if (!prevRoute) return true // New route
      
      // Compare geometry coordinates specifically
      const prevCoords = prevRoute.geometry?.coordinates
      const currCoords = currRoute.geometry?.coordinates
      
      if (!prevCoords && currCoords) return true
      if (prevCoords && !currCoords) return true
      
      if (prevCoords && currCoords) {
        // Compare coordinate arrays - even if same ID, geometry might have changed
        const prevCoordsStr = JSON.stringify(prevCoords)
        const currCoordsStr = JSON.stringify(currCoords)
        if (prevCoordsStr !== currCoordsStr) return true
      }
      
      // Also check if geometry type changed
      if (prevRoute.geometry?.type !== currRoute.geometry?.type) return true
    }
    
    return false
  }

  // Render routes
  useEffect(() => {
    // Deep equality check - only update if routes or selectedRoute actually changed
    const selectedRouteId = selectedRoute?.id
    const routesDeepChanged = !deepEqual(previousRoutesRef.current, routes)
    const routesGeometryChangedValue = routesGeometryChanged(previousRoutesRef.current, routes)
    const selectedRouteChanged = previousSelectedRouteIdRef.current !== selectedRouteId
    
    // Consider routes changed if deep equality differs OR geometry changed (for same IDs)
    const routesChanged = routesDeepChanged || routesGeometryChangedValue
    
    if (!routesChanged && !selectedRouteChanged) {
      return // Skip update - no changes
    }
    
    // Update stable refs
    previousRoutesRef.current = routes
    previousSelectedRouteIdRef.current = selectedRouteId
    stableRoutesRef.current = routes

    // Memoize route features with styling properties
    // Filter out routes without valid geometry
    const routeFeatures = stableRoutesRef.current
      .filter(route => {
        const hasGeometry = route.geometry && 
          (route.geometry.type === 'LineString' || route.geometry.type === 'MultiLineString') &&
          route.geometry.coordinates &&
          Array.isArray(route.geometry.coordinates) &&
          route.geometry.coordinates.length > 0
        
        if (!hasGeometry && route.id) {
        }
        
        return hasGeometry
      })
      .map(route => ({
        type: 'Feature' as const,
        properties: {
          id: route.id,
          name: route.name,
          description: route.description,
          distance: route.distance,
          duration: route.duration,
          is_public: route.is_public,
          created_at: route.created_at,
          // Styling properties
          color: route.color || '#3b82f6',
          line_type: route.line_type || 'solid',
          line_width: route.line_width || 4
        },
        geometry: route.geometry
      }))
    if (!map.current || !isLoaded || !styleLoaded) return

    if (!map.current.isStyleLoaded()) {
      map.current.once('styledata', () => {
        // Effect will re-run when styleLoaded updates
      })
      return
    }

    if (stableRoutesRef.current.length > 0 && routeFeatures.length === 0) {
    }

    try {
      const hasSource = !!map.current.getSource('route-lines')
      const hasLayer = !!map.current.getLayer('route-lines')

      if (hasSource) {
        const routeSource = map.current.getSource('route-lines') as mapboxgl.GeoJSONSource
        // Guard: Only update if we have features or if routes array is actually empty
        // This prevents clearing map data when routes exist but are temporarily filtered
        const hasRoutes = stableRoutesRef.current.length > 0
        const hasFeatures = routeFeatures.length > 0
        
        // Always update when routes changed (detected by deep equality check above)
        // This ensures geometry updates and proper clearing when routes are deleted
        const featuresToSet = hasFeatures ? routeFeatures : []
        
        // Update source data - this will clear the map if featuresToSet is empty
        routeSource.setData({
          type: 'FeatureCollection',
          features: featuresToSet
        })
        previousFeatureCountRef.current = featuresToSet.length
        
        // Re-add layer if it was removed
        if (!hasLayer) {
          map.current.addLayer({
            id: 'route-lines',
            type: 'line',
            source: 'route-lines',
            layout: {
              'line-join': 'round',
              'line-cap': 'round'
            },
            paint: {
              'line-color': [
                'coalesce',
                ['get', 'color'],
                '#3b82f6' // Default blue
              ] as any,
              'line-width': [
                'case',
                ['==', ['get', 'id'], selectedRoute?.id || null],
                [
                  'coalesce',
                  ['+', ['get', 'line_width'], 2],
                  6 // Selected: width + 2 or 6
                ],
                [
                  'coalesce',
                  ['get', 'line_width'],
                  4 // Non-selected: route width or 4
                ]
              ] as any,
              'line-opacity': 0.9,
              // Handle line type (solid, dashed, dotted)
              // For solid lines, use a very long dash pattern to effectively create a solid line
              'line-dasharray': [
                'case',
                ['==', ['get', 'line_type'], 'dashed'],
                ['literal', [4, 2]],
                ['==', ['get', 'line_type'], 'dotted'],
                ['literal', [2, 2]],
                // For solid or null, use a very long dash (effectively solid)
                ['literal', [1000, 0]]
              ] as any
            } as any
          })
        } else {
          // Update paint properties when route styling changes
          // Mapbox doesn't automatically update expressions, so we need to remove and re-add the layer
          // For now, we'll update the source data which should trigger a repaint
          // But we may need to force a layer update if styling changes
        }
      } else {
        map.current.addSource('route-lines', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: routeFeatures
          }
        })

        map.current.addLayer({
          id: 'route-lines',
          type: 'line',
          source: 'route-lines',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
            'visibility': 'visible'
          },
          paint: {
            'line-color': [
              'coalesce',
              ['get', 'color'],
              '#3b82f6' // Default blue
            ] as any,
            'line-width': [
              'case',
              ['==', ['get', 'id'], selectedRoute?.id || null],
              [
                'coalesce',
                ['+', ['get', 'line_width'], 2],
                6 // Selected: width + 2 or 6
              ],
              [
                'coalesce',
                ['get', 'line_width'],
                4 // Non-selected: route width or 4
              ]
            ] as any,
            'line-opacity': 0.9,
            // Handle line type (solid, dashed, dotted)
            // For solid lines, use a very long dash pattern to effectively create a solid line
            'line-dasharray': [
              'case',
              ['==', ['get', 'line_type'], 'dashed'],
              ['literal', [4, 2]],
              ['==', ['get', 'line_type'], 'dotted'],
              ['literal', [2, 2]],
              // For solid or null, use a very long dash (effectively solid)
              ['literal', [1000, 0]]
            ] as any
          } as any
        })

        // Add hover effects
        map.current.on('mouseenter', 'route-lines', () => {
          if (map.current) {
            map.current.getCanvas().style.cursor = 'pointer'
          }
        })

        map.current.on('mouseleave', 'route-lines', () => {
          if (map.current) {
            map.current.getCanvas().style.cursor = ''
          }
        })
        
        // Ensure layer is visible
        map.current.setLayoutProperty('route-lines', 'visibility', 'visible')
      }
      
      // When route styling changes, we need to remove and re-add the layer
      // because Mapbox expressions are evaluated at layer creation time
      const routeStyleKey = routeFeatures.map(r => 
        `${r.properties.id}:${r.properties.color}:${r.properties.line_type}:${r.properties.line_width}:${selectedRoute?.id || ''}`
      ).join('|')
      
      const styleChanged = previousStyleKeyRef.current !== routeStyleKey
      
      if (hasSource && hasLayer) {
        // Update source data first
        const routeSource = map.current.getSource('route-lines') as mapboxgl.GeoJSONSource
        const hasRoutes = stableRoutesRef.current.length > 0
        const hasFeatures = routeFeatures.length > 0
        const shouldUpdateSource = hasFeatures || !hasRoutes
        
        // Always update source data when routes changed (detected by deep equality check above)
        // This ensures proper clearing when routes are deleted
        const featuresToSet = hasFeatures ? routeFeatures : []
        routeSource.setData({
          type: 'FeatureCollection',
          features: featuresToSet
        })
        previousFeatureCountRef.current = featuresToSet.length
        
        // Only rebuild layer if styling changed (to avoid unnecessary rebuilds)
        if (styleChanged) {
          // Remove existing layer and handlers
          // Note: Removing the layer automatically removes its event handlers
          if (map.current.getLayer('route-lines')) {
            map.current.removeLayer('route-lines')
          }
          
          // Re-add layer with updated styling
          map.current.addLayer({
            id: 'route-lines',
            type: 'line',
            source: 'route-lines',
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
              'visibility': 'visible'
            },
            paint: {
              'line-color': [
                'coalesce',
                ['get', 'color'],
                '#3b82f6' // Default blue
              ] as any,
              'line-width': [
                'case',
                ['==', ['get', 'id'], selectedRoute?.id || null],
                [
                  'coalesce',
                  ['+', ['get', 'line_width'], 2],
                  6 // Selected: width + 2 or 6
                ],
                [
                  'coalesce',
                  ['get', 'line_width'],
                  4 // Non-selected: route width or 4
                ]
              ] as any,
            'line-opacity': 0.9,
            // Handle line type (solid, dashed, dotted)
            // For solid lines, use a very long dash pattern to effectively create a solid line
            'line-dasharray': [
              'case',
              ['==', ['get', 'line_type'], 'dashed'],
              ['literal', [4, 2]],
              ['==', ['get', 'line_type'], 'dotted'],
              ['literal', [2, 2]],
              // For solid or null, use a very long dash (effectively solid)
              ['literal', [1000, 0]]
            ] as any
          } as any
        })

        // Re-attach hover handlers
          map.current.on('mouseenter', 'route-lines', () => {
            if (map.current) {
              map.current.getCanvas().style.cursor = 'pointer'
            }
          })
          map.current.on('mouseleave', 'route-lines', () => {
            if (map.current) {
              map.current.getCanvas().style.cursor = ''
            }
          })
          
          // Update ref
          previousStyleKeyRef.current = routeStyleKey
        }
      }
      
      // Always track current style key to detect next change
      previousStyleKeyRef.current = routeStyleKey
    } catch (error) {
      if (map.current?.isStyleLoaded()) {
      }
    }
    // Note: routes and selectedRoute dependencies are checked via deep equality above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, isLoaded, styleLoaded, routes, selectedRoute])
}

